import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';
import WeeklyCalendar from '../../components/feature/WeeklyCalendar';

type FeastCategory = 'major' | 'minor' | 'fast';

type FeastEvent = {
  id: number;
  name: string;
  date: string;
  category: FeastCategory;
  icon: string;
  description: string;
  significance: string;
};

const MAJOR_FEASTS_KEYS = [
  { id: 1, nameKey: 'feast.nativity.name', dateKey: 'feast.nativity.date', descKey: 'feast.nativity.desc', sigKey: 'feast.nativity.significance', icon: 'ri-star-fill', category: 'major' as FeastCategory },
  { id: 2, nameKey: 'feast.theophany.name', dateKey: 'feast.theophany.date', descKey: 'feast.theophany.desc', sigKey: 'feast.theophany.significance', icon: 'ri-drop-fill', category: 'major' as FeastCategory },
  { id: 3, nameKey: 'feast.annunciation.name', dateKey: 'feast.annunciation.date', descKey: 'feast.annunciation.desc', sigKey: 'feast.annunciation.significance', icon: 'ri-heart-fill', category: 'major' as FeastCategory },
  { id: 4, nameKey: 'feast.palmSunday.name', dateKey: 'feast.palmSunday.date', descKey: 'feast.palmSunday.desc', sigKey: 'feast.palmSunday.significance', icon: 'ri-plant-fill', category: 'major' as FeastCategory },
  { id: 5, nameKey: 'feast.resurrection.name', dateKey: 'feast.resurrection.date', descKey: 'feast.resurrection.desc', sigKey: 'feast.resurrection.significance', icon: 'ri-sun-fill', category: 'major' as FeastCategory },
  { id: 6, nameKey: 'feast.ascension.name', dateKey: 'feast.ascension.date', descKey: 'feast.ascension.desc', sigKey: 'feast.ascension.significance', icon: 'ri-arrow-up-circle-fill', category: 'major' as FeastCategory },
  { id: 7, nameKey: 'feast.pentecost.name', dateKey: 'feast.pentecost.date', descKey: 'feast.pentecost.desc', sigKey: 'feast.pentecost.significance', icon: 'ri-fire-fill', category: 'major' as FeastCategory },
];

const MINOR_FEASTS_KEYS = [
  { id: 8, nameKey: 'feast.circumcision.name', dateKey: 'feast.circumcision.date', descKey: 'feast.circumcision.desc', sigKey: 'feast.circumcision.significance', icon: 'ri-calendar-check-fill', category: 'minor' as FeastCategory },
  { id: 9, nameKey: 'feast.presentation.name', dateKey: 'feast.presentation.date', descKey: 'feast.presentation.desc', sigKey: 'feast.presentation.significance', icon: 'ri-building-fill', category: 'minor' as FeastCategory },
  { id: 10, nameKey: 'feast.holyFamily.name', dateKey: 'feast.holyFamily.date', descKey: 'feast.holyFamily.desc', sigKey: 'feast.holyFamily.significance', icon: 'ri-map-pin-fill', category: 'minor' as FeastCategory },
  { id: 11, nameKey: 'feast.transfiguration.name', dateKey: 'feast.transfiguration.date', descKey: 'feast.transfiguration.desc', sigKey: 'feast.transfiguration.significance', icon: 'ri-flashlight-fill', category: 'minor' as FeastCategory },
  { id: 12, nameKey: 'feast.dormition.name', dateKey: 'feast.dormition.date', descKey: 'feast.dormition.desc', sigKey: 'feast.dormition.significance', icon: 'ri-heart-fill', category: 'minor' as FeastCategory },
  { id: 13, nameKey: 'feast.crossExaltation.name', dateKey: 'feast.crossExaltation.date', descKey: 'feast.crossExaltation.desc', sigKey: 'feast.crossExaltation.significance', icon: 'ri-add-circle-fill', category: 'minor' as FeastCategory },
  { id: 14, nameKey: 'feast.nayrouz.name', dateKey: 'feast.nayrouz.date', descKey: 'feast.nayrouz.desc', sigKey: 'feast.nayrouz.significance', icon: 'ri-seedling-fill', category: 'minor' as FeastCategory },
];

const FASTING_KEYS = [
  { id: 15, nameKey: 'feast.greatLent.name', dateKey: 'feast.greatLent.date', descKey: 'feast.greatLent.desc', sigKey: 'feast.greatLent.significance', icon: 'ri-moon-fill', category: 'fast' as FeastCategory },
  { id: 16, nameKey: 'feast.adventFast.name', dateKey: 'feast.adventFast.date', descKey: 'feast.adventFast.desc', sigKey: 'feast.adventFast.significance', icon: 'ri-moon-cloudy-fill', category: 'fast' as FeastCategory },
  { id: 17, nameKey: 'feast.apostlesFast.name', dateKey: 'feast.apostlesFast.date', descKey: 'feast.apostlesFast.desc', sigKey: 'feast.apostlesFast.significance', icon: 'ri-user-star-fill', category: 'fast' as FeastCategory },
  { id: 18, nameKey: 'feast.stMaryFast.name', dateKey: 'feast.stMaryFast.date', descKey: 'feast.stMaryFast.desc', sigKey: 'feast.stMaryFast.significance', icon: 'ri-heart-2-fill', category: 'fast' as FeastCategory },
  { id: 19, nameKey: 'feast.nineveh.name', dateKey: 'feast.nineveh.date', descKey: 'feast.nineveh.desc', sigKey: 'feast.nineveh.significance', icon: 'ri-anchor-fill', category: 'fast' as FeastCategory },
  { id: 20, nameKey: 'feast.wedFri.name', dateKey: 'feast.wedFri.date', descKey: 'feast.wedFri.desc', sigKey: 'feast.wedFri.significance', icon: 'ri-calendar-line', category: 'fast' as FeastCategory },
];

export default function CalendarPage() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<'all' | 'major' | 'minor' | 'fast'>('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Build translated feast arrays
  const majorFeasts: FeastEvent[] = MAJOR_FEASTS_KEYS.map(f => ({ id: f.id, name: t(f.nameKey), date: t(f.dateKey), category: f.category, icon: f.icon, description: t(f.descKey), significance: t(f.sigKey) }));
  const minorFeasts: FeastEvent[] = MINOR_FEASTS_KEYS.map(f => ({ id: f.id, name: t(f.nameKey), date: t(f.dateKey), category: f.category, icon: f.icon, description: t(f.descKey), significance: t(f.sigKey) }));
  const fastingPeriods: FeastEvent[] = FASTING_KEYS.map(f => ({ id: f.id, name: t(f.nameKey), date: t(f.dateKey), category: f.category, icon: f.icon, description: t(f.descKey), significance: t(f.sigKey) }));

  const allEvents = [...majorFeasts, ...minorFeasts, ...fastingPeriods];

  const filteredEvents =
    activeFilter === 'all'
      ? allEvents
      : activeFilter === 'major'
      ? majorFeasts
      : activeFilter === 'minor'
      ? minorFeasts
      : fastingPeriods;

  const getCategoryStyles = (category: FeastCategory) => {
    switch (category) {
      case 'major': return { bg: 'bg-[#722f37]', text: 'text-white', light: 'bg-[#722f37]/10', border: 'border-[#722f37]/20', accent: 'text-[#722f37]' };
      case 'minor': return { bg: 'bg-[#d4a017]', text: 'text-white', light: 'bg-[#d4a017]/10', border: 'border-[#d4a017]/20', accent: 'text-[#d4a017]' };
      case 'fast': return { bg: 'bg-stone-600', text: 'text-white', light: 'bg-stone-100', border: 'border-stone-200', accent: 'text-stone-600' };
    }
  };

  const getCategoryLabel = (category: FeastCategory) => {
    switch (category) {
      case 'major': return t('calendarPage.category.major');
      case 'minor': return t('calendarPage.category.minor');
      case 'fast': return t('calendarPage.category.fast');
    }
  };

  const tabs = [
    { key: 'all', label: t('calendarPage.tab.all'), color: 'bg-gray-900', count: allEvents.length },
    { key: 'major', label: t('calendarPage.tab.major'), color: 'bg-[#722f37]', count: majorFeasts.length },
    { key: 'minor', label: t('calendarPage.tab.minor'), color: 'bg-[#d4a017]', count: minorFeasts.length },
    { key: 'fast', label: t('calendarPage.tab.fast'), color: 'bg-stone-600', count: fastingPeriods.length },
  ];

  return (
    <PageLayout hasHero>
      <PageHero
        title={t('calendarPage.title')}
        subtitle={t('calendarPage.subtitle')}
        backgroundImage="/assets/readdy/6d247c00-e018-481e-b5fa-7b567fed711c_IMG_0502-2-edb7e1e8b433.jpeg"
      />

      <PageSectionNav
        sections={[
          { id: 'weekly-schedule', label: t('calendarPage.weeklySchedule') },
          { id: 'feast-days', label: t('calendarPage.feastDaysFasting') },
        ]}
      />

      {/* Weekly Schedule Section */}
      <section id="weekly-schedule" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-sm text-[#d4a017] mb-2 uppercase tracking-wider font-semibold">{t('calendarPage.weeklySchedule')}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-3">
              {t('calendarPage.thisWeekServices')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('calendarPage.weeklyScheduleDesc')}
            </p>
          </div>
          <WeeklyCalendar />
        </div>
      </section>

      {/* Feast Days & Fasting Section */}
      <section id="feast-days" className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">

          {/* Section Header */}
          <div className="text-center mb-10">
            <p className="text-sm text-[#d4a017] mb-2 uppercase tracking-wider font-semibold">{t('calendarPage.liturgicalYear')}</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-3">
              {t('calendarPage.feastDaysFasting')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('calendarPage.liturgicalYearDesc')}
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-[#722f37]/10 rounded-xl flex-shrink-0">
                <i className="ri-star-fill text-[#722f37] text-2xl"></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">7</p>
                <p className="text-sm text-gray-600">{t('calendarPage.stats.majorFeasts')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-[#d4a017]/10 rounded-xl flex-shrink-0">
                <i className="ri-sun-fill text-[#d4a017] text-2xl"></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">7</p>
                <p className="text-sm text-gray-600">{t('calendarPage.stats.minorFeasts')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-xl flex-shrink-0">
                <i className="ri-moon-fill text-stone-600 text-2xl"></i>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">210+</p>
                <p className="text-sm text-gray-600">{t('calendarPage.stats.fastingDays')}</p>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex justify-center mb-8 px-2">
            <div className="flex flex-wrap justify-center bg-white rounded-2xl sm:rounded-full p-1.5 sm:p-1 shadow-sm border border-gray-200 gap-1.5 sm:gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key as typeof activeFilter)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 sm:gap-2 ${
                    activeFilter === tab.key
                      ? `${tab.color} text-white`
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                  <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full ${activeFilter === tab.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Section Label */}
          {activeFilter !== 'all' && (
            <div className="mb-6">
              {activeFilter === 'major' && (
                <div className="bg-[#722f37]/5 border border-[#722f37]/15 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-[#722f37] text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong className="text-[#722f37]">{t('calendarPage.majorInfoTitle')}</strong> {t('calendarPage.majorInfoDesc')}
                  </p>
                </div>
              )}
              {activeFilter === 'minor' && (
                <div className="bg-[#d4a017]/5 border border-[#d4a017]/15 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-[#d4a017] text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong className="text-[#d4a017]">{t('calendarPage.minorInfoTitle')}</strong> {t('calendarPage.minorInfoDesc')}
                  </p>
                </div>
              )}
              {activeFilter === 'fast' && (
                <div className="bg-stone-100 border border-stone-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-stone-600 text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong className="text-stone-700">{t('calendarPage.fastInfoTitle')}</strong> {t('calendarPage.fastInfoDesc')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Events Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {filteredEvents.map((event, index) => {
              const styles = getCategoryStyles(event.category);
              const isExpanded = expandedId === event.id;
              return (
                <div
                  key={event.id}
                  className="bg-white rounded-xl border border-gray-100 hover:shadow-lg transition-all overflow-hidden"
                >
                  <div
                    className="p-5 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : event.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div>
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${styles.bg} ${styles.text} mb-1.5`}>
                            {getCategoryLabel(event.category)}
                          </span>
                          <h4 className="text-base font-bold text-gray-900 leading-snug">{event.name}</h4>
                        </div>
                        <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full ${styles.light} transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                          <i className={`ri-arrow-down-s-line ${styles.accent} text-sm`}></i>
                        </div>
                      </div>
                      <p className={`text-sm font-semibold ${styles.accent} flex items-center gap-1 mb-2`}>
                        <i className="ri-calendar-line"></i>
                        {event.date}
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">{event.description}</p>
                    </div>
                  </div>

                  {/* Expanded Significance */}
                  {isExpanded && (
                    <div className={`px-5 pb-5 pt-0`}>
                      <div className={`${styles.light} ${styles.border} border rounded-lg p-4`}>
                        <p className={`text-xs font-bold uppercase tracking-wider ${styles.accent} mb-2 flex items-center gap-1`}>
                          <i className="ri-bookmark-fill"></i>
                          {t('calendarPage.spiritualSignificance')}
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed">{event.significance}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Info Note */}
          <div className="mt-10 bg-gradient-to-r from-[#722f37]/5 to-[#d4a017]/5 rounded-2xl p-6 border border-[#d4a017]/20">
            <div className="flex flex-col md:flex-row items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-[#722f37] rounded-full flex-shrink-0">
                <i className="ri-information-line text-white text-2xl"></i>
              </div>
              <div className="text-center md:text-left">
                <h4 className="text-lg font-bold text-gray-900 mb-1">{t('calendarPage.aboutCopticCalendar')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('calendarPage.calendarInfo')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stay Updated CTA */}
      <section className="py-20 bg-gradient-to-br from-[#3d1a1e] to-[#2a1215]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-6">
            <i className="ri-newspaper-line text-3xl text-amber-400"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">{t('calendarPage.stayUpdated')}</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('calendarPage.stayUpdatedDesc')}
          </p>
          <a
            href="/news"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-amber-500 text-[#3d1a1e] font-semibold rounded-full hover:bg-amber-400 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <span>{t('calendarPage.goToNews')}</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>
    </PageLayout>
  );
}
